import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronDown,
  Flame,
  Menu,
  MessageCircle,
  Play,
  ShoppingBag,
  Sparkles,
  X,
} from 'lucide-react';
import Brand from './components/Brand';
import Reveal from './components/Reveal';
import { photos } from './photos';

const copy = {
  sv: {
    nav: ['Upplägget', 'Träningen', 'Maten'],
    start: 'Börja nu',
    eyebrow: 'En plan som faktiskt passar din vardag',
    heroA: 'DIN STARKASTE',
    heroB: 'VECKA BÖRJAR',
    heroC: 'HÄR.',
    intro:
      'Fitmaister samlar pass, måltider och pepp i ett enkelt flöde. Du får veta vad du ska göra i dag — utan att först planera hela livet.',
    primary: 'Skapa min plan',
    secondary: 'Se hur det fungerar',
    proof: ['Personlig veckoplan', '500+ guidade pass', '1000+ enkla recept'],
    dashboard: {
      hello: 'God morgon, Elin',
      sub: 'Du är helt i fas den här veckan.',
      next: 'Nästa på din plan',
      workout: 'Helkroppsstyrka',
      time: '28 min · Medelhög',
      play: 'Starta pass',
      score: 'Veckans rytm',
      meals: 'Måltider',
      sessions: 'Pass',
    },
    strip: ['Rätt pass', 'Rätt mat', 'Rätt tempo', 'En enda plan'],
    systemKick: 'Så blir det enkelt',
    systemTitle: 'Mindre funderande. Mer gjort.',
    systemSub:
      'Varje del i Fitmaister leder vidare till nästa. Träningen påverkar maten, maten bygger inköpslistan och din vecka justeras längs vägen.',
    steps: [
      ['01', 'Berätta var du står', 'Välj mål, nivå, matvanor och hur mycket tid du faktiskt har. Det tar bara några minuter.'],
      ['02', 'Få en vecka som håller', 'Du får en tydlig rytm av korta pass, smarta måltider och återhämtning på rätt dagar.'],
      ['03', 'Justera medan du lever', 'Missat ett pass eller ändrat middagsplanerna? Din coach flyttar om utan dåligt samvete.'],
    ],
    trainingKick: 'Träning, utan tröskel',
    trainingTitle: 'Tryck på play. Resten är redan tänkt.',
    trainingSub:
      'Filmade pass visar tempo, teknik och alternativ. Träna hemma, ute eller på gymmet — planen anpassas efter platsen och tiden du har.',
    tags: ['10–45 minuter', 'Hem eller gym', 'Alla nivåer', 'Tydlig videoguide'],
    coachTitle: 'Fråga coachen precis som du tänker',
    coachSub:
      '“Jag sov dåligt och har tjugo minuter. Vad gör jag?” Du får ett konkret svar som passar dagens energi och veckans mål.',
    coachReply: 'Vi byter till ett lugnt överkroppspass på 18 minuter. Jag flyttar benpasset till fredag.',
    nutritionKick: 'Mat som jobbar med dig',
    nutritionTitle: 'Öppna kylen. Börja där.',
    nutritionSub:
      'Fitmaister bygger recept av det du redan har och visar portion, näring och tillagningstid direkt. Mindre svinn, färre spontanköp.',
    recipe: 'Krämig citronpasta',
    recipeMeta: ['24 min', '34 g protein', '560 kcal'],
    pantry: 'Matchar 7 av 8 varor hemma',
    foodCards: [
      ['Smart logg', 'Fota tallriken eller välj ett recept. Dagens energi uppdateras direkt.'],
      ['Veckans lista', 'Alla ingredienser samlas och sorteras så att handlingen går fortare.'],
      ['Prisöverblick', 'Jämför totalsumman hos anslutna butiker innan du går hemifrån.'],
    ],
    closeKick: 'Din vecka, på ditt sätt',
    closeTitle: 'Det behöver inte vara perfekt för att ge resultat.',
    closeSub: 'Börja där du är. Fitmaister hjälper dig att ta nästa rimliga steg, varje dag.',
    legal:
      'Fitmaister är en tjänst för träning, kost, recept och coachning. Prenumerationen kostar 200 kronor per månad och debiteras via mobilräkningen eller dras från ditt förbetalda saldo. Den förnyas varje månad tills du säger upp den. Avsluta genom att skicka STOPP FIT till 72300. Du får alltid en gratis bekräftelse. Åldersgräns 18 år. Frågor: help@niffomobile.com.',
    terms: 'Regler och villkor',
    privacy: 'Integritetspolicy',
    contact: 'Kontakt',
    rights: 'Alla rättigheter förbehållna.',
  },
  en: {
    nav: ['The method', 'Training', 'Food'],
    start: 'Get started',
    eyebrow: 'A plan built for your real life',
    heroA: 'YOUR STRONGEST',
    heroB: 'WEEK STARTS',
    heroC: 'HERE.',
    intro:
      'Fitmaister brings workouts, meals and motivation into one clear flow. Know what to do today — without planning your whole life first.',
    primary: 'Build my plan',
    secondary: 'See how it works',
    proof: ['Personal weekly plan', '500+ guided sessions', '1000+ simple recipes'],
    dashboard: {
      hello: 'Good morning, Elin',
      sub: 'You are right on track this week.',
      next: 'Next on your plan',
      workout: 'Full-body strength',
      time: '28 min · Medium',
      play: 'Start session',
      score: 'Weekly rhythm',
      meals: 'Meals',
      sessions: 'Sessions',
    },
    strip: ['The right session', 'The right food', 'Your pace', 'One clear plan'],
    systemKick: 'How it gets easier',
    systemTitle: 'Less deciding. More doing.',
    systemSub:
      'Every part of Fitmaister leads naturally to the next. Training shapes your food, food builds the list, and your week adapts as you go.',
    steps: [
      ['01', 'Tell us where you are', 'Choose your goal, level, food habits and the time you actually have. It only takes a few minutes.'],
      ['02', 'Get a week that works', 'A clear rhythm of short sessions, practical meals and recovery on the days you need it.'],
      ['03', 'Adapt as life happens', 'Missed a session or changed dinner plans? Your coach rearranges things without the guilt.'],
    ],
    trainingKick: 'Training, minus the friction',
    trainingTitle: 'Press play. The thinking is already done.',
    trainingSub:
      'Guided videos show pace, technique and alternatives. Train at home, outside or at the gym — the plan fits your place and your time.',
    tags: ['10–45 minutes', 'Home or gym', 'Every level', 'Clear video guides'],
    coachTitle: 'Ask your coach the way you think',
    coachSub:
      '“I slept badly and have twenty minutes. What should I do?” Get a useful answer that fits today’s energy and this week’s goal.',
    coachReply: 'Let’s do an easy 18-minute upper-body session. I moved your leg day to Friday.',
    nutritionKick: 'Food that works with you',
    nutritionTitle: 'Open the fridge. Start there.',
    nutritionSub:
      'Fitmaister turns what you already have into recipes, with portions, nutrition and cooking time upfront. Less waste, fewer impulse buys.',
    recipe: 'Creamy lemon pasta',
    recipeMeta: ['24 min', '34 g protein', '560 kcal'],
    pantry: 'Matches 7 of 8 items at home',
    foodCards: [
      ['Quick logging', 'Photograph your plate or choose a recipe. Today’s energy updates immediately.'],
      ['One weekly list', 'Every ingredient is gathered and sorted to make shopping faster.'],
      ['Price overview', 'Compare the total at participating shops before leaving home.'],
    ],
    closeKick: 'Your week, your way',
    closeTitle: 'It does not need to be perfect to make progress.',
    closeSub: 'Start where you are. Fitmaister helps you take the next realistic step, every day.',
    legal:
      'Fitmaister is a service for training, food, recipes and coaching. The subscription costs 200 Swedish kronor per month and is charged to your mobile bill or taken from your prepaid balance. It renews each month until you cancel. Cancel by sending STOPP FIT to 72300. You always receive a free confirmation. Age limit 18. Questions: help@niffomobile.com.',
    terms: 'Terms and conditions',
    privacy: 'Privacy policy',
    contact: 'Contact',
    rights: 'All rights reserved.',
  },
};

const navTargets = ['method', 'training', 'food'];

function App() {
  const [lang, setLang] = useState('sv');
  const [menuOpen, setMenuOpen] = useState(false);
  const t = copy[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#f7f7f2] text-[#10130f]">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-black/10 bg-[#f7f7f2]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[76px] max-w-[1380px] items-center justify-between px-5 lg:px-10">
          <Brand />
          <nav className="hidden items-center gap-9 lg:flex">
            {t.nav.map((item, index) => (
              <a key={item} href={`#${navTargets[index]}`} className="nav-link">
                {item}
              </a>
            ))}
          </nav>
          <div className="hidden items-center gap-3 sm:flex">
            <button
              onClick={() => setLang(lang === 'sv' ? 'en' : 'sv')}
              className="lang-button"
              aria-label="Change language"
            >
              {lang === 'sv' ? 'SV' : 'EN'} <ChevronDown size={14} />
            </button>
            <a href="/s" className="button button-dark !py-3 !px-5">
              {t.start} <ArrowRight size={16} />
            </a>
          </div>
          <button className="sm:hidden" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {menuOpen && (
          <div className="border-t border-black/10 bg-[#f7f7f2] p-5 sm:hidden">
            {t.nav.map((item, index) => (
              <a key={item} href={`#${navTargets[index]}`} onClick={() => setMenuOpen(false)} className="block border-b border-black/10 py-4 font-bold">
                {item}
              </a>
            ))}
            <div className="mt-5 flex gap-3">
              <button onClick={() => setLang(lang === 'sv' ? 'en' : 'sv')} className="lang-button">
                {lang === 'sv' ? 'SV' : 'EN'}
              </button>
              <a href="/s" className="button button-dark flex-1">{t.start}</a>
            </div>
          </div>
        )}
      </header>

      <main>
        <section className="hero-grid overflow-hidden pt-[76px]">
          <div className="mx-auto grid max-w-[1380px] lg:min-h-[790px] lg:grid-cols-[1.05fr_.95fr]">
            <div className="flex flex-col justify-center px-5 py-16 lg:px-10 lg:py-24">
              <Reveal>
                <div className="eyebrow"><Sparkles size={15} /> {t.eyebrow}</div>
                <h1 className="hero-title mt-7">
                  <span>{t.heroA}</span>
                  <span>{t.heroB}</span>
                  <span className="text-[#6957ff]">{t.heroC}</span>
                </h1>
                <p className="mt-7 max-w-xl text-lg leading-relaxed text-black/60 sm:text-xl">{t.intro}</p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <a href="/s" className="button button-purple">{t.primary} <ArrowRight size={18} /></a>
                  <a href="#method" className="button button-outline"><Play size={17} fill="currentColor" /> {t.secondary}</a>
                </div>
                <div className="mt-10 flex flex-col gap-3 text-sm font-bold text-black/65 sm:flex-row sm:gap-7">
                  {t.proof.map((item) => <span key={item} className="flex items-center gap-2"><Check size={16} className="text-[#6957ff]" />{item}</span>)}
                </div>
              </Reveal>
            </div>

            <div className="relative min-h-[620px] overflow-hidden bg-[#d8ff63] lg:min-h-0">
              <img src={photos.lift} alt="" className="absolute inset-0 h-full w-full object-cover grayscale" />
              <div className="absolute inset-0 bg-[#6957ff]/20 mix-blend-multiply" />
              <div className="absolute inset-x-4 bottom-5 rounded-[28px] bg-white p-5 shadow-2xl sm:inset-x-8 sm:bottom-8 sm:p-7">
                <div className="flex items-start justify-between">
                  <div><p className="text-xl font-extrabold">{t.dashboard.hello}</p><p className="mt-1 text-sm text-black/50">{t.dashboard.sub}</p></div>
                  <div className="grid h-11 w-11 place-items-center rounded-full bg-[#d8ff63]"><Flame size={20} /></div>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-[1.45fr_.8fr]">
                  <div className="rounded-2xl bg-[#11150f] p-5 text-white">
                    <p className="text-[11px] font-bold uppercase tracking-[.18em] text-white/50">{t.dashboard.next}</p>
                    <p className="mt-3 text-xl font-extrabold">{t.dashboard.workout}</p>
                    <p className="mt-1 text-sm text-white/55">{t.dashboard.time}</p>
                    <button className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#d8ff63] px-4 py-3 text-sm font-extrabold text-black">
                      <Play size={14} fill="currentColor" /> {t.dashboard.play}
                    </button>
                  </div>
                  <div className="rounded-2xl bg-[#efeee8] p-5">
                    <p className="text-[11px] font-bold uppercase tracking-[.18em] text-black/45">{t.dashboard.score}</p>
                    <p className="mt-3 text-4xl font-black">84<span className="text-base text-black/35">%</span></p>
                    <div className="mt-5 flex h-14 items-end gap-1">
                      {[42, 62, 53, 88, 72, 96, 84].map((height, i) => <span key={i} className="flex-1 rounded-t bg-[#6957ff]" style={{ height: `${height}%` }} />)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="overflow-hidden border-y border-black/10 bg-[#d8ff63] py-4">
          <div className="marquee-track">
            {[...t.strip, ...t.strip].map((item, i) => <span key={i}>{item}<span>✦</span></span>)}
          </div>
        </div>

        <section id="method" className="section-space scroll-mt-20">
          <div className="mx-auto max-w-[1380px] px-5 lg:px-10">
            <Reveal>
              <p className="section-kicker">{t.systemKick}</p>
              <div className="mt-5 grid gap-6 lg:grid-cols-2">
                <h2 className="section-title">{t.systemTitle}</h2>
                <p className="max-w-xl text-lg leading-relaxed text-black/55 lg:justify-self-end">{t.systemSub}</p>
              </div>
            </Reveal>
            <div className="mt-14 grid gap-px overflow-hidden rounded-[30px] border border-black/10 bg-black/10 lg:grid-cols-3">
              {t.steps.map(([number, title, body], i) => (
                <Reveal key={number} delay={i * 80}>
                  <article className="h-full bg-white p-7 sm:p-9">
                    <span className="grid h-12 w-12 place-items-center rounded-full bg-[#6957ff] text-sm font-black text-white">{number}</span>
                    <h3 className="mt-10 text-2xl font-extrabold">{title}</h3>
                    <p className="mt-4 leading-relaxed text-black/55">{body}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="training" className="scroll-mt-20 bg-[#11150f] text-white">
          <div className="mx-auto grid max-w-[1380px] lg:grid-cols-2">
            <div className="relative min-h-[560px] overflow-hidden lg:min-h-[760px]">
              <img src={photos.hiit} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
              <div className="absolute bottom-7 left-7 right-7 rounded-3xl border border-white/20 bg-black/45 p-5 backdrop-blur-md sm:bottom-10 sm:left-10 sm:right-auto sm:w-80">
                <div className="flex items-center gap-4">
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#d8ff63] text-black"><Play fill="currentColor" /></div>
                  <div><p className="font-extrabold">Core & cardio</p><p className="text-sm text-white/55">22:00 · Level 2</p></div>
                </div>
                <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-white/15"><div className="h-full w-[38%] bg-[#d8ff63]" /></div>
              </div>
            </div>
            <div className="flex flex-col justify-center px-5 py-16 lg:px-16 lg:py-24">
              <Reveal>
                <p className="section-kicker text-[#d8ff63]">{t.trainingKick}</p>
                <h2 className="section-title mt-5">{t.trainingTitle}</h2>
                <p className="mt-6 text-lg leading-relaxed text-white/60">{t.trainingSub}</p>
                <div className="mt-8 flex flex-wrap gap-2">
                  {t.tags.map((tag) => <span key={tag} className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold">{tag}</span>)}
                </div>
              </Reveal>
              <Reveal delay={100}>
                <div className="mt-12 rounded-[28px] bg-[#24281f] p-6 sm:p-7">
                  <div className="flex items-center gap-3"><MessageCircle className="text-[#d8ff63]" /><h3 className="text-xl font-extrabold">{t.coachTitle}</h3></div>
                  <p className="mt-4 text-sm leading-relaxed text-white/55">{t.coachSub}</p>
                  <div className="mt-5 rounded-2xl bg-[#6957ff] p-4 text-sm font-semibold leading-relaxed">{t.coachReply}</div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section id="food" className="section-space scroll-mt-20 bg-[#efeef8]">
          <div className="mx-auto max-w-[1380px] px-5 lg:px-10">
            <div className="grid items-center gap-12 lg:grid-cols-[.85fr_1.15fr] lg:gap-20">
              <Reveal>
                <p className="section-kicker">{t.nutritionKick}</p>
                <h2 className="section-title mt-5">{t.nutritionTitle}</h2>
                <p className="mt-6 text-lg leading-relaxed text-black/55">{t.nutritionSub}</p>
                <a href="/s" className="button button-purple mt-8">{t.primary}<ArrowRight size={18} /></a>
              </Reveal>
              <Reveal delay={100}>
                <div className="relative overflow-hidden rounded-[36px] bg-white p-3 shadow-xl shadow-[#6957ff]/10">
                  <img src={photos.fresh} alt="" className="h-[460px] w-full rounded-[27px] object-cover" />
                  <div className="absolute inset-x-7 bottom-7 rounded-3xl bg-white/95 p-5 backdrop-blur sm:inset-x-auto sm:left-8 sm:w-[380px]">
                    <div className="flex items-center justify-between gap-3"><h3 className="text-xl font-extrabold">{t.recipe}</h3><span className="rounded-full bg-[#d8ff63] px-3 py-1 text-xs font-black">96% MATCH</span></div>
                    <div className="mt-4 flex flex-wrap gap-2">{t.recipeMeta.map((item) => <span key={item} className="rounded-full bg-[#efeee8] px-3 py-1.5 text-xs font-bold">{item}</span>)}</div>
                    <p className="mt-4 flex items-center gap-2 text-sm font-bold text-[#6957ff]"><Check size={16} /> {t.pantry}</p>
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="mt-16 grid gap-4 md:grid-cols-3">
              {[BarChart3, ShoppingBag, Sparkles].map((Icon, i) => (
                <Reveal key={t.foodCards[i][0]} delay={i * 70}>
                  <article className="h-full rounded-[26px] bg-white p-7">
                    <Icon className="text-[#6957ff]" size={27} />
                    <h3 className="mt-8 text-xl font-extrabold">{t.foodCards[i][0]}</h3>
                    <p className="mt-3 leading-relaxed text-black/55">{t.foodCards[i][1]}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#6957ff] text-white">
          <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full border-[70px] border-white/10" />
          <div className="absolute -bottom-40 -left-24 h-96 w-96 rounded-full border-[70px] border-[#d8ff63]/20" />
          <div className="relative mx-auto max-w-[1100px] px-5 py-24 text-center lg:py-32">
            <Reveal>
              <p className="section-kicker text-[#d8ff63]">{t.closeKick}</p>
              <h2 className="mx-auto mt-6 max-w-4xl text-5xl font-black leading-[.96] tracking-[-.055em] sm:text-7xl">{t.closeTitle}</h2>
              <p className="mx-auto mt-7 max-w-2xl text-lg text-white/70">{t.closeSub}</p>
              <a href="/s" className="button mx-auto mt-9 w-fit bg-[#d8ff63] text-black hover:bg-white">{t.primary}<ArrowRight size={18} /></a>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="bg-[#11150f] text-white">
        <div className="mx-auto max-w-[1380px] px-5 py-12 lg:px-10">
          <p className="max-w-5xl text-xs leading-relaxed text-white/45">{t.legal}</p>
          <div className="mt-10 flex flex-col gap-8 border-t border-white/10 pt-8 lg:flex-row lg:items-end lg:justify-between">
            <div><Brand light /><p className="mt-4 text-xs text-white/35">© 2026 Fitmaister. {t.rights}</p></div>
            <nav className="flex flex-wrap gap-x-7 gap-y-3 text-sm font-bold">
              <a href="/regler-och-villkor.html" className="hover:text-[#d8ff63]">{t.terms}</a>
              <a href="/integritetspolicy.html" className="hover:text-[#d8ff63]">{t.privacy}</a>
              <a href="mailto:help@niffomobile.com" className="hover:text-[#d8ff63]">{t.contact}</a>
            </nav>
            <div className="text-sm leading-relaxed text-white/55">
              <p className="font-bold text-white">Niffo Mobile B.V.</p>
              <p>Stationsplein 28, 1382 AD Weesp, Netherlands</p>
              <a href="mailto:help@niffomobile.com">help@niffomobile.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
